/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TYPE } from '@/theme/typography';
import {
  Modal, Paper, Box, TextField, Typography, ListItemButton, ListItemIcon,
  ListItemText, List, InputAdornment, Chip, Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp as RevenueIcon,
  TrendingDown as ExpenseIcon,
  People as HRIcon,
  Payments as PayrollIcon,
  Business as GroupIcon,
  Settings as SettingsIcon,
  Apartment as FacilityIcon,
  Search as SearchIcon,
  AccountBalance as LedgerIcon,
  BarChart as BudgetIcon,
  Security as AuditIcon,
  SmartToy as AIIcon,
  EventNote as LeaveIcon,
  ListAlt as RefDataIcon,
  Extension as ModulesIcon,
  CheckCircle as ConsolidationIcon,
  History as RecentIcon,
  FlashOn as ActionIcon,
  PersonAdd as PersonAddIcon,
  Add as AddIcon,
  PlayArrow as RunIcon,
} from '@mui/icons-material';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';

// ── Types ──

interface CommandItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  section: string;
  /** Quick actions navigate and optionally include a query param to auto-open a dialog */
  action?: boolean;
}

// ── Recent Items (localStorage) ──

const RECENT_KEY = 'etg:recent-pages';
const MAX_RECENT = 8;

function getRecentPaths(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch {
    return [];
  }
}

function addRecentPath(path: string) {
  const recent = getRecentPaths().filter(p => p !== path);
  recent.unshift(path);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

// ── Component ──

export default function CommandPalette() {
  const navigate = useNavigate();
  const location = useLocation();
  const commandPaletteOpen = useUIStore((s) => s.commandPaletteOpen);
  const setCommandPaletteOpen = useUIStore((s) => s.setCommandPaletteOpen);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Track page visits for "Recent" section
  useEffect(() => {
    if (location.pathname && location.pathname !== '/') {
      addRecentPath(location.pathname);
    }
  }, [location.pathname]);

  // Auth state for role/module filtering
  // user was previously consulted for is_system_admin; Sprint 4b removed it.
  // Leaving the subscription out entirely — platformRole below covers admin UX.
  const companies = useAuthStore((s) => s.companies);
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const activeCompany = companies.find((c) => c.company_id === activeCompanyId);

  const isSystemAdmin = false; // Sprint 4b: removed; retained as const for downstream branches
  const isGroupAdmin = activeCompany?.is_group_level ?? false;
  const isAdmin = isSystemAdmin || isGroupAdmin;
  const platformRole = useAuthStore((s) => s.platformRole);
  const enabledModules = useAuthStore((s) => s.getEnabledModules());
  const isModuleEnabled = (key: string) => enabledModules.includes(key);

  // Build navigation items
  const NAV_ITEMS: CommandItem[] = useMemo(() => {
    const items: CommandItem[] = [
      { id: 'dash', label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', section: 'Pages' },
      { id: 'profile', label: 'Profile', icon: <HRIcon />, path: '/profile', section: 'Pages' },
    ];

    if (isModuleEnabled('accounting')) {
      items.push(
        { id: 'revenue', label: 'Revenue', icon: <RevenueIcon />, path: '/accounting/revenue', section: 'Finance' },
        { id: 'expenses', label: 'Expenses', icon: <ExpenseIcon />, path: '/accounting/expenses', section: 'Finance' },
        { id: 'journal', label: 'Journal Entries', icon: <LedgerIcon />, path: '/accounting/journal-entries', section: 'Finance' },
        { id: 'invoices', label: 'Invoices', icon: <LedgerIcon />, path: '/accounting/invoices', section: 'Finance' },
      );
    }

    if (isModuleEnabled('hr')) {
      items.push(
        { id: 'employees', label: 'Employees', icon: <HRIcon />, path: '/hr/employees', section: 'People' },
        { id: 'leave', label: 'Leave', icon: <LeaveIcon />, path: '/hr/leave', section: 'People' },
        { id: 'payroll', label: 'Payroll', icon: <PayrollIcon />, path: '/payroll', section: 'People' },
        { id: 'onboarding', label: 'Onboarding', icon: <HRIcon />, path: '/hr/onboarding', section: 'People' },
      );
    }

    if (isModuleEnabled('recruitment')) {
      items.push(
        { id: 'recruitment', label: 'Recruitment', icon: <HRIcon />, path: '/hr/recruitment', section: 'People' },
        { id: 'job-postings', label: 'Job Postings', icon: <HRIcon />, path: '/hr/job-postings', section: 'People' },
      );
    }

    if (isModuleEnabled('performance')) {
      items.push(
        { id: 'performance', label: 'Performance Reviews', icon: <HRIcon />, path: '/performance', section: 'People' },
      );
    }

    if (isModuleEnabled('tenants')) {
      items.push(
        { id: 'tenants', label: 'Tenants', icon: <FacilityIcon />, path: '/tenants', section: 'Facility' },
        { id: 'leases', label: 'Leases', icon: <FacilityIcon />, path: '/tenants/leases', section: 'Facility' },
        { id: 'locations', label: 'Locations', icon: <FacilityIcon />, path: '/tenants/locations', section: 'Facility' },
      );
    }

    if (isModuleEnabled('budget')) {
      items.push(
        { id: 'budgets', label: 'Budgets', icon: <BudgetIcon />, path: '/budget', section: 'Budget' },
      );
    }

    if (isAdmin && isModuleEnabled('group')) {
      items.push(
        { id: 'companies', label: 'Companies', icon: <GroupIcon />, path: '/group/companies', section: 'Group' },
        { id: 'consolidation', label: 'Consolidation', icon: <ConsolidationIcon />, path: '/group/consolidated', section: 'Group' },
        { id: 'intercompany', label: 'Intercompany', icon: <GroupIcon />, path: '/group/intercompany', section: 'Group' },
      );
    }

    if (isAdmin) {
      items.push(
        { id: 'audit', label: 'Audit Logs', icon: <AuditIcon />, path: '/audit', section: 'Admin' },
        { id: 'users', label: 'Users & Roles', icon: <HRIcon />, path: '/settings#users', section: 'Settings' },
        { id: 'reference', label: 'Reference Data', icon: <RefDataIcon />, path: '/settings#reference-data', section: 'Settings' },
        { id: 'import', label: 'Data Import', icon: <SettingsIcon />, path: '/settings#import', section: 'Settings' },
      );
    }

    if (isSystemAdmin) {
      items.push(
        { id: 'modules', label: 'Modules', icon: <ModulesIcon />, path: '/settings#modules', section: 'Settings' },
        { id: 'billing', label: 'Billing & Plans', icon: <PayrollIcon />, path: '/settings#billing-plans', section: 'Settings' },
      );
    }

    items.push(
      { id: 'company-settings', label: 'Company Settings', icon: <SettingsIcon />, path: '/settings', section: 'Settings' },
    );

    if (isModuleEnabled('ai')) {
      items.push(
        { id: 'ai-chat', label: 'AI Chat', icon: <AIIcon />, path: '/ai/chat', section: 'Intelligence' },
      );
    }

    if (platformRole) {
      items.push(
        { id: 'platform-overview', label: 'Platform · Overview', icon: <AuditIcon />, path: '/platform/overview', section: 'Platform' },
        { id: 'platform-tenants', label: 'Platform · Tenants', icon: <GroupIcon />, path: '/platform/tenants', section: 'Platform' },
        { id: 'platform-trials', label: 'Platform · Trials', icon: <HRIcon />, path: '/platform/trials', section: 'Platform' },
        { id: 'platform-dunning', label: 'Platform · Dunning', icon: <PayrollIcon />, path: '/platform/dunning', section: 'Platform' },
        { id: 'platform-invoices', label: 'Platform · Invoices', icon: <LedgerIcon />, path: '/platform/invoices', section: 'Platform' },
        { id: 'platform-gateways', label: 'Platform · Gateways', icon: <SettingsIcon />, path: '/platform/gateways', section: 'Platform' },
        { id: 'platform-emails', label: 'Platform · Emails', icon: <HRIcon />, path: '/platform/emails', section: 'Platform' },
        { id: 'platform-analytics', label: 'Platform · Analytics', icon: <BudgetIcon />, path: '/platform/analytics', section: 'Platform' },
        { id: 'platform-flags', label: 'Platform · Flags', icon: <SettingsIcon />, path: '/platform/flags', section: 'Platform' },
        { id: 'platform-audit', label: 'Platform · Audit', icon: <AuditIcon />, path: '/platform/audit', section: 'Platform' },
        { id: 'platform-admins', label: 'Platform · Admins', icon: <SettingsIcon />, path: '/platform/admins', section: 'Platform' },
      );
    }

    return items;
  }, [isAdmin, isSystemAdmin, platformRole, isModuleEnabled]);

  // Build quick actions
  const QUICK_ACTIONS: CommandItem[] = useMemo(() => {
    const actions: CommandItem[] = [];

    if (isModuleEnabled('hr')) {
      actions.push(
        { id: 'act-new-employee', label: 'Create Employee', icon: <PersonAddIcon />, path: '/hr/employees?action=create', section: 'Quick Actions', action: true },
      );
    }
    if (isModuleEnabled('accounting')) {
      actions.push(
        { id: 'act-new-revenue', label: 'Record Revenue', icon: <AddIcon />, path: '/accounting/revenue?action=create', section: 'Quick Actions', action: true },
        { id: 'act-new-expense', label: 'Record Expense', icon: <AddIcon />, path: '/accounting/expenses?action=create', section: 'Quick Actions', action: true },
      );
    }
    if (isModuleEnabled('hr')) {
      actions.push(
        { id: 'act-run-payroll', label: 'Run Payroll', icon: <RunIcon />, path: '/payroll?action=run', section: 'Quick Actions', action: true },
      );
    }

    return actions;
  }, [isModuleEnabled]);

  // Build recent items from localStorage
  const RECENT_ITEMS: CommandItem[] = useMemo(() => {
    const recentPaths = getRecentPaths();
    const allItems = [...NAV_ITEMS, ...QUICK_ACTIONS];
    return recentPaths
      .map(path => allItems.find(item => item.path === path || item.path.split('?')[0] === path))
      .filter(Boolean)
      .slice(0, 5) as CommandItem[];
  }, [NAV_ITEMS, QUICK_ACTIONS]); // re-evaluate when palette opens

  // Combined list: when no search, show Recent → Quick Actions → All
  // When searching, filter all items
  const displayItems = useMemo(() => {
    if (search.trim()) {
      const q = search.toLowerCase();
      return [...QUICK_ACTIONS, ...NAV_ITEMS].filter(
        item => item.label.toLowerCase().includes(q) || item.section.toLowerCase().includes(q)
      );
    }

    // No search: show sections
    const sections: { title: string; items: CommandItem[] }[] = [];
    if (RECENT_ITEMS.length > 0) {
      sections.push({ title: 'Recent', items: RECENT_ITEMS });
    }
    if (QUICK_ACTIONS.length > 0) {
      sections.push({ title: 'Quick Actions', items: QUICK_ACTIONS });
    }
    sections.push({ title: 'Navigate', items: NAV_ITEMS });

    return sections;
  }, [search, NAV_ITEMS, QUICK_ACTIONS, RECENT_ITEMS]);

  // Flatten for keyboard navigation
  const flatItems: CommandItem[] = useMemo(() => {
    if (Array.isArray(displayItems) && displayItems.length > 0 && 'path' in displayItems[0]) {
      return displayItems as CommandItem[];
    }
    return (displayItems as { title: string; items: CommandItem[] }[])
      .flatMap(s => s.items);
  }, [displayItems]);

  const handleSelect = useCallback((item: CommandItem) => {
    navigate(item.path);
    addRecentPath(item.path.split('?')[0]);
    setCommandPaletteOpen(false);
    setSearch('');
    setSelectedIndex(0);
  }, [navigate, setCommandPaletteOpen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % flatItems.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + flatItems.length) % flatItems.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatItems[selectedIndex]) {
        handleSelect(flatItems[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setCommandPaletteOpen(false);
    }
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [setCommandPaletteOpen]);

  // Track flat index for highlight
  let flatIdx = -1;

  const isSectionView = !search.trim();

  return (
    <Modal
      open={commandPaletteOpen}
      onClose={() => setCommandPaletteOpen(false)}
      aria-label="Command palette"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        pt: '100px',
      }}
    >
      <Paper
        sx={{
          width: 560,
          maxHeight: 600,
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            fullWidth
            placeholder="Search pages, actions..."
            autoFocus
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: TYPE.lg,
              },
            }}
          />
        </Box>

        <List
          sx={{
            maxHeight: 500,
            overflow: 'auto',
            py: 0,
          }}
        >
          {flatItems.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No results found
              </Typography>
            </Box>
          ) : isSectionView ? (
            // Grouped view with section headers
            (displayItems as { title: string; items: CommandItem[] }[]).map((section) => (
              <Box key={section.title}>
                <Typography
                  sx={{
                    px: 2, pt: 1.5, pb: 0.5,
                    fontSize: TYPE.xs,
                    fontWeight: TYPE.semibold,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {section.title === 'Recent' && <RecentIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />}
                  {section.title === 'Quick Actions' && <ActionIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />}
                  {section.title}
                </Typography>
                {section.items.map((item) => {
                  flatIdx++;
                  const idx = flatIdx;
                  return (
                    <ListItemButton
                      key={item.id + '-' + section.title}
                      onClick={() => handleSelect(item)}
                      selected={idx === selectedIndex}
                      sx={{
                        py: 0.75,
                        px: 2,
                        '&.Mui-selected': { bgcolor: 'action.selected' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 28, mr: 1.5, color: item.action ? 'primary.main' : 'text.secondary' }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: TYPE.base, fontWeight: item.action ? TYPE.semibold : TYPE.medium }}
                      />
                      {item.action && (
                        <Chip label="Action" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                      )}
                    </ListItemButton>
                  );
                })}
                {section.title !== 'Navigate' && <Divider sx={{ my: 0.5 }} />}
              </Box>
            ))
          ) : (
            // Flat search results
            flatItems.map((item, index) => (
              <ListItemButton
                key={item.id}
                onClick={() => handleSelect(item)}
                selected={index === selectedIndex}
                sx={{
                  py: 0.75,
                  px: 2,
                  '&.Mui-selected': { bgcolor: 'action.selected' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28, mr: 1.5, color: item.action ? 'primary.main' : 'text.secondary' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  secondary={item.section}
                  primaryTypographyProps={{ fontSize: TYPE.base, fontWeight: item.action ? TYPE.semibold : TYPE.medium }}
                  secondaryTypographyProps={{ fontSize: TYPE.xs }}
                />
                {item.action && (
                  <Chip label="Action" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                )}
              </ListItemButton>
            ))
          )}
        </List>

        <Box
          sx={{
            p: 1.5,
            bgcolor: 'background.default',
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1.5,
            justifyContent: 'flex-end',
            fontSize: TYPE.xs,
            color: 'text.secondary',
          }}
        >
          <span>↑↓ Navigate</span>
          <span>↲ Select</span>
          <span>Esc Close</span>
        </Box>
      </Paper>
    </Modal>
  );
}
